import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Factory, ArrowLeft, TrendingUp, Clock, Users } from 'lucide-react';

const ManufacturingCaseStudy = () => {
  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <article className="pt-40 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#3B82F6] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-600 to-red-600 rounded-xl flex items-center justify-center">
                <Factory className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-sm text-[#2563EB] font-semibold uppercase tracking-wide">
                  Manufacturing
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC]">
                  PrecisionTech Industries
                </h1>
              </div>
            </div>
            <p className="text-xl text-[#94A3B8]">
              Restoring 32% margins and reducing inventory 20% through intelligent pricing
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden mb-12 border border-[#2563EB]/20">
            <img
              src="https://images.unsplash.com/photo-1758630737900-a28682c5aa69"
              alt="Manufacturing Success"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/60 to-transparent" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Users className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">500+</div>
              <div className="text-sm text-[#94A3B8]">Industrial components</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <TrendingUp className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">32%</div>
              <div className="text-sm text-[#94A3B8]">Margins restored</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Clock className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">20%</div>
              <div className="text-sm text-[#94A3B8]">Inventory reduction</div>
            </div>
          </div>

          <div className="prose prose-invert prose-lg max-w-none">
            <div className="text-[#94A3B8] leading-relaxed space-y-8">
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-red-500/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-4">The Challenge</h2>
                <p className="text-lg">
                  Raw material costs increased 15% while competitive pressure prevented passing costs to customers. Margins compressed from 32% to 18% in 18 months, threatening profitability.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-4">The Solution</h2>
                <p className="text-lg">
                  Dynamic input cost pass through strategies with demand sensing. Real time market intelligence to optimize timing and magnitude of price adjustments.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-green-500/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-6">The Results</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">32%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Margins Restored</div>
                  </div>
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">20%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Inventory Reduction</div>
                  </div>
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">8 weeks</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Faster Adjustments</div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#2563EB]/10 to-transparent border border-[#2563EB]/30 rounded-xl p-8">
                <div className="text-4xl text-[#2563EB] mb-4">"</div>
                <p className="text-[#F8FAFC] text-2xl leading-relaxed mb-6 italic">
                  We maintained profitability through volatile commodity markets. The system paid for itself in the first quarter alone.
                </p>
                <div className="border-t border-[#2563EB]/20 pt-4">
                  <div className="font-semibold text-[#F8FAFC] text-lg">James Mitchell</div>
                  <div className="text-[#94A3B8]">VP of Operations</div>
                  <div className="text-sm text-[#2563EB] mt-1">PrecisionTech Industries</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 p-8 bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl text-center">
            <h3 className="text-3xl font-bold text-[#F8FAFC] mb-4">
              Optimize Manufacturing Pricing
            </h3>
            <p className="text-[#94A3B8] mb-6 text-lg">
              Navigate volatile markets with intelligent pricing strategies.
            </p>
            <Link to="/pricing" className="inline-block bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold transition-colors text-lg">
              Explore Pricing Plans
            </Link>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default ManufacturingCaseStudy;
